/**
 * AttackPathFormModal — wraps the shared FormModal with the Step 5
 * attack-path field schema. Two external option sources are injected
 * at render time:
 *
 *   - `threatOptions` — from Step 4 threats list (threat_id select)
 *   - `assetOptions`  — from Step 2 assets   list (asset_id select)
 *
 * `preconditions` round-trips as a semicolon-separated text field;
 * the consumer's handleSave converts to `string[]` via
 * `normaliseAttackPathRecord` before calling `crud.add` / `update`.
 */
import React, { useMemo } from 'react';
import FormModal from '../../../../components/ui/FormModal/FormModal';
import { ATTACK_PATH_FORM_FIELDS, type AttackPathRecord } from './attackPathFieldSchema';

const FormModalAny = FormModal as unknown as React.ComponentType<any>;

export interface OptionLite {
  value: string;
  label: string;
}

export interface AttackPathFormModalProps {
  mode: 'add' | 'edit';
  initialPath?: AttackPathRecord | null;
  threatOptions: OptionLite[];
  assetOptions: OptionLite[];
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}

export default function AttackPathFormModal({
  mode,
  initialPath,
  threatOptions,
  assetOptions,
  onSubmit,
  onClose,
}: AttackPathFormModalProps) {
  const isEdit = mode === 'edit';

  // The form's preconditions textarea takes a string; the underlying
  // record stores `string[]`. Pre-populate by joining with `; `.
  const initialValues = useMemo(() => {
    if (!initialPath) return {};
    const { preconditions, ...rest } = initialPath as Record<string, unknown>;
    return {
      ...rest,
      preconditions: Array.isArray(preconditions) ? preconditions.join('; ') : preconditions ?? '',
    };
  }, [initialPath]);

  const fields = useMemo(
    () =>
      ATTACK_PATH_FORM_FIELDS.map((f) => {
        if (f.name === 'threat_id') return { ...f, options: threatOptions };
        if (f.name === 'asset_id') return { ...f, options: assetOptions };
        if (isEdit && f.name === 'attack_path_id') return { ...f, disabled: true };
        return f;
      }),
    [threatOptions, assetOptions, isEdit],
  );

  return (
    <FormModalAny
      title={isEdit ? 'Edit Attack Path' : 'Add Attack Path'}
      fields={fields}
      initialValues={initialValues}
      submitLabel={isEdit ? 'Save Changes' : 'Add Attack Path'}
      submittingLabel={isEdit ? 'Saving…' : 'Adding…'}
      onSubmit={onSubmit}
      onClose={onClose}
      maxWidth={760}
    />
  );
}
